<script setup lang="ts">
  import { IconName, Typo } from '@travelata-libs/ui-kit';
  import { useIntersectionObserver, whenever } from '@vueuse/core';
  import { computed, nextTick, ref, shallowRef, watch, onServerPrefetch, onMounted } from 'vue';
  import { yandexTrackingService, type LeadBannerTriggerReason } from '@/entities/analytics';
  import type { Product } from '@/entities/product';
  import { getSingleHotelId, withAvailableDirectFlight, type SearchCriteria } from '@/entities/searchCriteria';
  import type { SearchFormGoal, SearchFormTracking } from '@/entities/searchForm';
  import type { SwitchCity } from '@/features/switchCity';
  import { IndexPage } from '@/pages/index';
  import { useTravelataAppConfig, type FlipDirection } from '@/shared/composables';
  import { PagesUrl, type PageContent } from '@/shared/config/pages';
  import { getClientUuid } from '@/shared/lib/clientUuid';
  import { getCurrentUrl } from '@/shared/lib/urlQuery';
  import type { DateRange, DepartureCity } from '@/shared/model';
  import FlipView from '@/shared/ui/FlipView.vue';
  import FloatingChipButton from '@/shared/ui/FloatingChipButton.vue';
  import { HotelsMap } from '@/widgets/hotelsMap';
  import { LowPriceCalendar, useLowPriceCalendar } from '@/widgets/lowPriceCalendar';
  import { SearchFormBottomSheet } from '@/widgets/searchForm';
  import SerpAlternativeSearchNotice from './SerpAlternativeSearchNotice.vue';
  import SerpFiltersMobile from './SerpFiltersMobile.vue';
  import SerpFiltersResetToast from './SerpFiltersResetToast.vue';
  import SerpList from './SerpList.vue';
  import SerpMobileHeader from './SerpMobileHeader.vue';
  import SerpQuickFilters from './SerpQuickFilters.vue';
  import SerpQuickFilterSheet from './SerpQuickFilterSheet.vue';
  import SerpQuickFiltersSkeleton from './SerpQuickFiltersSkeleton.vue';
  import SerpSearchExpiredSheet from './SerpSearchExpiredSheet.vue';
  import SerpSearchProgress from './SerpSearchProgress.vue';
  import SerpShortLeadBanner from './SerpShortLeadBanner.vue';
  import SerpSortSheet from './SerpSortSheet.vue';
  import { SERP_SEO_MIN_PRODUCTS, SERP_SHORT_MAX_CARDS } from '../config/list';
  import { DEFAULT_SERP_SORT } from '../config/sort';
  import { SERP_MAP_TEXTS } from '../config/texts';
  import {
    isEmiratesDirection,
    withAvailableEmirates,
    withEmiratesDirection,
    withEmiratesOperators,
    withoutEmiratesOperators,
  } from '../lib/emiratesFilter';
  import { isFilterModelSelected } from '../lib/filtersModel';
  import { isCountryChanged } from '../lib/filtersReset';
  import { getSerpHotelPageQuery, type SerpHotelPageParams } from '../lib/hotelPageUrl';
  import { getNoticeLeadBannerTriggerReason } from '../lib/leadBannerTriggerReason';
  import {
    isSearchCriteriaFiltersChanged,
    isSearchCriteriaFiltersSelected,
    setSearchCriteriaFilters,
  } from '../lib/searchFilters';
  import { formatSearchSubtitle, formatSearchTitle } from '../lib/searchSummary';
  import { fromSerpUrl } from '../lib/serpUrl';
  import { sortSerpProducts } from '../lib/sortSerpProducts';
  import type { AlternativeSearchView } from '../model/alternativeSearch/types';
  import { getEmiratesDirectionSource } from '../model/emiratesSearch';
  import { useSerp } from '../model/serpContainer';
  import {
    type SerpCardOpenOptions,
    type SerpFilterGroupId,
    type SerpFilterGroupView,
    type SerpHotelOpenOptions,
    type SerpProduct,
    type SerpSort,
  } from '../model/types';
  import { useAlternativeSearch } from '../model/useAlternativeSearch';
  import { useEmptySearchGoal } from '../model/useEmptySearchGoal';
  import { useFiltersReset } from '../model/useFiltersReset';
  import { useHeaderCollapse } from '../model/useHeaderCollapse';
  import { useLeadBannerSession } from '../model/useLeadBannerSession';
  import { useMaxViewedPosition } from '../model/useMaxViewedPosition';
  import { useSearchExpiration } from '../model/useSearchExpiration';
  import { useSearchProgress } from '../model/useSearchProgress';
  import { useSearchSession } from '../model/useSearchSession';
  import { useSerpDataLayer } from '../model/useSerpDataLayer';
  import { useSerpEcommerce } from '../model/useSerpEcommerce';
  import { useSerpFilters } from '../model/useSerpFilters';
  import { useSerpFiltersDraft } from '../model/useSerpFiltersDraft';
  import { useSerpHitTracking } from '../model/useSerpHitTracking';
  import { useSerpMindbox } from '../model/useSerpMindbox';
  import { useSerpResults } from '../model/useSerpResults';
  import { useSerpTracking } from '../model/useSerpTracking';
  import { useSerpUrl } from '../model/useSerpUrl';

  interface SerpLayoutMobileProps {
    cities?: SwitchCity[];
    currentCity?: SwitchCity | null;
    isVisibleRoomButton?: boolean;
    pageContent?: PageContent;
  }

  withDefaults(defineProps<SerpLayoutMobileProps>(), {
    cities: () => [],
    currentCity: null,
    isVisibleRoomButton: true,
    pageContent: undefined,
  });

  interface SerpLayoutMobileEmits {
    (e: 'chat'): void;
    (e: 'change:city', city: SwitchCity): void;
  }

  const emit = defineEmits<SerpLayoutMobileEmits>();

  const { useSearchCriteria, productSearch } = useSerp();

  const { model, prices, formUrl, resortsUrl, saveSearch, getFormData } = useSearchCriteria();

  const { products, dictionaries, isSearching, isLoading } = useSerpResults(productSearch);

  const { value: progressValue, stage: progressStage, isVisible: isProgressVisible } = useSearchProgress(productSearch);

  const serpFilters = useSerpFilters(products, model, dictionaries);
  const {
    filtersModel,
    filteredProducts,
    selectedCount: filtersCount,
    quickFilters,
    filtersGroups,
    selectedFilters,
    getFilterGroup,
    isDraftChanged: isFiltersDraftChanged,
    isGroupDraftChanged: isFilterGroupDraftChanged,
    isDraftSelected: isFiltersDraftSelected,
    searchCriteriaDraft,
    syncDraft: syncFiltersDraft,
    toggle: toggleFilterDraft,
    clearGroup: clearFilterGroupDraft,
    apply: applyFiltersModelDraft,
    replace: replaceFilters,
  } = serpFilters;

  const lowPriceCalendar = useLowPriceCalendar(model);

  const {
    view: alternativeSearchView,
    alternativeSearchCriteria,
    userSearchUuid,
    search: searchWithAlternatives,
    selectDepartureCity,
  } = useAlternativeSearch(productSearch, { formUrl, calendar: lowPriceCalendar });

  const serpTracking = useSerpTracking(productSearch, {
    userSearchUuid,
    calendar: lowPriceCalendar,
    products,
    filters: serpFilters,
  });

  const serpMindbox = useSerpMindbox(productSearch, { userSearchUuid });

  const { viewCard, getMaxViewedPosition } = useMaxViewedPosition(productSearch);

  const serpHitTracking = useSerpHitTracking(model);

  useLeadBannerSession(productSearch);

  const {
    message: filtersResetMessage,
    resetFiltersForNewCountry,
    restore: restoreResetFilters,
    dismiss: dismissFiltersReset,
  } = useFiltersReset(productSearch, { userSearchUuid, filtersModel, replace: replaceFilters, dictionaries });

  const { isExpired: isSearchExpired, dismiss: dismissSearchExpiration } = useSearchExpiration(productSearch);

  const noticeView = computed<AlternativeSearchView | null>(() => {
    if (products.value.length && !filteredProducts.value.length) return { kind: 'notFound' };

    const view = alternativeSearchView.value;

    return view && (view.kind === 'notFound' || products.value.length) ? view : null;
  });

  const noticeLeadTriggerReason = computed<LeadBannerTriggerReason>(() =>
    getNoticeLeadBannerTriggerReason(alternativeSearchView.value),
  );

  const alternativeSearchNoticeRef = ref<InstanceType<typeof SerpAlternativeSearchNotice> | null>(null);

  useEmptySearchGoal(
    computed(() => alternativeSearchNoticeRef.value?.$el as HTMLElement | undefined),
    computed(() => alternativeSearchView.value?.kind === 'similar' || alternativeSearchView.value?.kind === 'notFound'),
  );

  const lastSearchCriteria = shallowRef<SearchCriteria | null>(null);

  const resultsSearchCriteria = computed<SearchCriteria>(
    () => alternativeSearchCriteria.value ?? lastSearchCriteria.value ?? model.value,
  );

  const isAlternativeSearchResults = computed<boolean>(() => alternativeSearchCriteria.value !== null);

  const isShortSerp = computed<boolean>(
    () =>
      !isSearching.value &&
      !isAlternativeSearchResults.value &&
      products.value.length <= SERP_SHORT_MAX_CARDS &&
      filteredProducts.value.length > 0,
  );

  const userSearchCriteria = computed<SearchCriteria>(() => lastSearchCriteria.value ?? model.value);

  const isCalendarLoading = computed<boolean>(() => isLoading.value && !alternativeSearchCriteria.value);

  const isHomeOpen = ref(false);
  const isSerpHidden = ref(false);
  const homeFlipDirection = computed<FlipDirection>(() => (isHomeOpen.value ? 'forward' : 'back'));
  const isMapOpen = ref(false);
  const isHeaderCollapsed = useHeaderCollapse(computed<boolean>(() => isMapOpen.value || isHomeOpen.value));
  const isSearchSheetOpen = ref(false);
  const modelDraft = shallowRef<SearchCriteria>(model.value);
  const isSortSheetOpen = ref(false);
  const isFiltersSheetOpen = ref(false);
  const isSeoVisible = ref(true);
  const seoAnchorRef = ref<HTMLElement | null>(null);

  useIntersectionObserver(seoAnchorRef, ([entry]) => {
    isSeoVisible.value = !entry?.isIntersecting;
  });
  const quickFilterGroupId = ref<SerpFilterGroupId | null>(null);
  const isFilterSheetOpen = computed<boolean>(() => isFiltersSheetOpen.value || quickFilterGroupId.value !== null);
  const quickFilterGroup = computed<SerpFilterGroupView | null>(() =>
    quickFilterGroupId.value ? getFilterGroup(quickFilterGroupId.value) : null,
  );
  const isQuickFilterDraftChanged = computed<boolean>(() =>
    quickFilterGroupId.value ? isFilterGroupDraftChanged(quickFilterGroupId.value) : false,
  );

  const isSearchExpiredSheetVisible = computed<boolean>(
    () =>
      isSearchExpired.value &&
      !isHomeOpen.value &&
      !isSearchSheetOpen.value &&
      !isSortSheetOpen.value &&
      !isFilterSheetOpen.value,
  );

  useSerpFiltersDraft(serpFilters, model, isFilterSheetOpen);
  const quickFiltersScrollLeft = ref(0);
  let searchStarted = false;
  let isLastSearchEmirates = false;

  const urlState = fromSerpUrl(getCurrentUrl());

  const sort = ref<SerpSort>(urlState.sort ?? DEFAULT_SERP_SORT);

  const serpDataLayer = useSerpDataLayer(productSearch, { filters: serpFilters, sort });

  if (urlState.filtersModel) replaceFilters(urlState.filtersModel);

  const { searchId, startSearchSession } = useSearchSession(urlState.searchId);

  useSerpUrl({ criteria: model, filtersModel, sort, searchId });

  const sortedProducts = computed<SerpProduct[]>(() => sortSerpProducts(filteredProducts.value, sort.value));

  const calendarProducts = computed<Product[]>(() =>
    alternativeSearchCriteria.value ? [] : products.value.flatMap((serpProduct) => serpProduct.products),
  );

  const mapProducts = computed<Product[]>(() => filteredProducts.value.flatMap((serpProduct) => serpProduct.products));

  const serpListKey = computed<string>(() => `${sort.value}:${JSON.stringify(filtersModel.value)}`);

  watch(serpListKey, () => {
    nextTick(() => window.scrollTo({ top: 0 }));
  });

  const serpListVisibleCount = ref(0);

  const serpEcommerce = useSerpEcommerce(productSearch, {
    products: sortedProducts,
    listKey: serpListKey,
    visibleCount: serpListVisibleCount,
    criteria: userSearchCriteria,
  });

  const title = computed<string>(() => formatSearchTitle(model.value));
  const subtitle = computed<string>(() => formatSearchSubtitle(model.value));

  let listScrollTop = 0;
  let isListChangedOnMap = false;

  watch(sortedProducts, () => {
    if (isMapOpen.value) isListChangedOnMap = true;
  });

  const openMap = (): void => {
    listScrollTop = window.scrollY;
    isListChangedOnMap = false;
    isMapOpen.value = true;
    nextTick(() => window.scrollTo(0, 0));
  };

  const closeMap = (): void => {
    const scrollTop = isListChangedOnMap ? 0 : listScrollTop;

    isMapOpen.value = false;
    nextTick(() => window.scrollTo(0, scrollTop));
  };

  const openSearchSheet = (): void => {
    modelDraft.value = model.value;
    isSearchSheetOpen.value = true;
  };

  const closeSearchSheet = (): void => {
    isSearchSheetOpen.value = false;
  };

  const openSortSheet = (): void => {
    isSortSheetOpen.value = true;
  };

  const openFiltersSheet = (): void => {
    serpDataLayer.trackFiltersOpen();
    isFiltersSheetOpen.value = true;
  };

  const closeSortSheet = (): void => {
    isSortSheetOpen.value = false;
  };

  const resetEmiratesOperators = (criteria: SearchCriteria): void => {
    const isEmirates = isEmiratesDirection(criteria);

    if (!isEmirates && (criteria.block || isLastSearchEmirates)) {
      replaceFilters(withoutEmiratesOperators(filtersModel.value));
    }

    isLastSearchEmirates = isEmirates;
  };

  const getHotelPageUrl = (
    hotelId: number,
    params: Pick<SerpHotelPageParams, 'serpProduct' | 'criteria' | 'toReviews'>,
  ): string => {
    const query = getSerpHotelPageQuery({
      ...params,
      filtersModel: filtersModel.value,
      sort: sort.value,
      searchId: searchId.value,
      searchUuid: productSearch.searchUuid,
    });
    const { baseHost } = useTravelataAppConfig();

    return `${baseHost}/${PagesUrl.GotoHotel}/${hotelId}#?${query}`;
  };

  const redirectToHotel = (hotelId: number, criteria: SearchCriteria): void => {
    window.location.href = getHotelPageUrl(hotelId, { serpProduct: null, criteria, toReviews: false });
  };

  const runSearch = (criteria: SearchCriteria, { withLowLimit }: { withLowLimit?: boolean } = {}): void => {
    const searchCriteria = withAvailableEmirates(withAvailableDirectFlight(criteria));
    const hotelId = searchStarted ? getSingleHotelId(searchCriteria.destination) : null;

    if (hotelId) {
      redirectToHotel(hotelId, searchCriteria);
      return;
    }

    searchStarted = true;
    lastSearchCriteria.value = searchCriteria;

    if (searchCriteria !== criteria) {
      model.value = { ...model.value, directFlight: searchCriteria.directFlight, block: searchCriteria.block };
    }

    resetEmiratesOperators(criteria);
    startSearchSession(searchCriteria);
    serpDataLayer.trackSearchStart(searchCriteria, searchId.value);
    serpHitTracking.trackSearchStart(searchCriteria);
    searchWithAlternatives(searchCriteria, withLowLimit);
    nextTick(() => window.scrollTo({ top: 0 }));
  };

  const onSearch = (value: SearchCriteria): void => {
    saveSearch(value);
    runSearch(value);
  };

  const searchFromForm = (value: SearchCriteria): void => {
    const criteria = isCountryChanged(lastSearchCriteria.value, value) ? resetFiltersForNewCountry(value) : value;

    model.value = criteria;
    onSearch(criteria);
  };

  const openHome = (): void => {
    isHomeOpen.value = true;
  };

  const onBack = (): void => {
    if (isMapOpen.value) {
      closeMap();
      return;
    }

    openHome();
  };

  const hideSerpUnderHome = (): void => {
    if (!isHomeOpen.value) return;

    isSerpHidden.value = true;
    window.scrollTo({ top: 0 });
  };

  const onHomeSearch = (criteria: SearchCriteria): void => {
    isSerpHidden.value = false;
    isHomeOpen.value = false;
    searchFromForm(criteria);
  };

  const SEARCH_FORM_TRACKING: SearchFormTracking = { pageType: 'serp', productType: 'tour' };

  const trackSearchClick = (goal: SearchFormGoal, formSource: 'header_edit' | 'back'): void => {
    yandexTrackingService.setTrackingGoal(goal.name, 'click', {
      ...goal.params,
      page_type: SEARCH_FORM_TRACKING.pageType,
      form_source: formSource,
      client_uuid: getClientUuid(),
      sid: searchId.value,
    });
  };

  const onSelectDepartureCity = (city: DepartureCity): void => {
    if (isLastSearchEmirates && !isEmiratesDirection({ fromCity: city, destination: model.value.destination })) {
      replaceFilters(withoutEmiratesOperators(filtersModel.value));
    }

    selectDepartureCity(city);
  };

  let isEmiratesSearchPending = false;

  const searchEmirates = async (): Promise<void> => {
    if (isEmiratesSearchPending) return;

    isEmiratesSearchPending = true;
    const source = await getEmiratesDirectionSource(model.value.destination, { formUrl, resortsUrl });
    isEmiratesSearchPending = false;

    const criteria = withEmiratesDirection(model.value, source);

    if (!criteria) return;

    model.value = criteria;
    replaceFilters(withEmiratesOperators(filtersModel.value));
    onSearch(criteria);
  };

  const refreshExpiredSearch = (): void => {
    runSearch(model.value);
  };

  const onCalendarSelect = (dateRange: DateRange): void => {
    model.value = { ...model.value, dateRange };
    onSearch(model.value);
  };

  const applyFilters = (): void => {
    applyFiltersModelDraft();

    if (!isSearchCriteriaFiltersChanged(model.value, searchCriteriaDraft.value)) return;

    model.value = setSearchCriteriaFilters(model.value, searchCriteriaDraft.value);

    if (searchStarted) runSearch(model.value);
  };

  const applyFiltersSheet = (): void => {
    const isSearchFiltersChanged = isSearchCriteriaFiltersChanged(model.value, searchCriteriaDraft.value);

    applyFilters();

    if (isSearchFiltersChanged) serpDataLayer.trackFiltersApplied(filtersModel.value.isExclusive);
  };

  const restoreFilters = (): void => {
    const searchCriteriaFilters = restoreResetFilters();

    if (!searchCriteriaFilters || !isSearchCriteriaFiltersSelected(searchCriteriaFilters)) return;

    model.value = setSearchCriteriaFilters(model.value, searchCriteriaFilters);
    runSearch(model.value);
  };

  const closeFiltersSheet = (): void => {
    isFiltersSheetOpen.value = false;
  };

  const openQuickFilterSheet = (groupId: SerpFilterGroupId): void => {
    quickFilterGroupId.value = groupId;
  };

  const closeQuickFilterSheet = (): void => {
    quickFilterGroupId.value = null;
  };

  const saveQuickFiltersScroll = (scrollLeft: number): void => {
    quickFiltersScrollLeft.value = scrollLeft;
  };

  const toggleQuickFilter = (groupId: SerpFilterGroupId): void => {
    syncFiltersDraft();
    toggleFilterDraft(groupId, groupId);
    applyFilters();
  };

  const clearQuickFilter = (groupId: SerpFilterGroupId): void => {
    syncFiltersDraft();
    clearFilterGroupDraft(groupId);
    applyFilters();
  };

  const openHotel = (serpProduct: SerpProduct, { toReviews }: SerpHotelOpenOptions): void => {
    const url = getHotelPageUrl(serpProduct.hotel.id, {
      serpProduct,
      criteria: resultsSearchCriteria.value,
      toReviews,
    });

    window.open(url, '_blank');
  };

  const openListHotel = (serpProduct: SerpProduct, options: SerpCardOpenOptions): void => {
    const position = sortedProducts.value.indexOf(serpProduct) + 1;

    serpTracking.trackHotelOpen(serpProduct, { products: sortedProducts.value, sort: sort.value });
    serpMindbox.trackHotelOpen(serpProduct, {
      criteria: resultsSearchCriteria.value,
      searchCriteria: lastSearchCriteria.value,
      filtersModel: filtersModel.value,
      dictionaries: dictionaries.value,
    });
    serpEcommerce.trackProductClick(serpProduct);
    serpDataLayer.trackHotelClick(serpProduct, position);
    yandexTrackingService.serpCardClick({
      target: options.target,
      hotelId: serpProduct.hotel.id,
      position,
      maxViewedPosition: getMaxViewedPosition(),
      clientUuid: getClientUuid(),
      sid: searchId.value,
      searchUuid: productSearch.searchUuid ?? '',
    });
    openHotel(serpProduct, options);
  };

  const saveListVisibleCount = (count: number): void => {
    serpListVisibleCount.value = count;
  };

  const openMapHotel = (product: Product, options: SerpHotelOpenOptions): void => {
    const serpProduct = filteredProducts.value.find(({ hotel }) => hotel.id === product.hotel?.id);

    if (serpProduct) openHotel(serpProduct, options);
  };

  onServerPrefetch(async () => {
    await getFormData();
  });

  onMounted(() => {
    whenever(
      () => model.value.destination && model.value.destination.kind !== 'none',
      () => {
        const isFiltersModelSelected = isFilterModelSelected(urlState.filtersModel);
        saveSearch(model.value);
        runSearch(model.value, { withLowLimit: !isFiltersModelSelected });
      },
      { immediate: true, once: true },
    );
  });
</script>

<template>
  <div
    class="serp-layout-mobile"
    :class="{
      'serp-layout-mobile_map': isMapOpen && !isHomeOpen,
      'serp-layout-mobile_header-collapsed': isHeaderCollapsed,
    }"
    data-testid="serp-layout-mobile"
  >
    <FlipView
      :direction="homeFlipDirection"
      edge="left"
      overlay
      @after-enter="hideSerpUnderHome"
    >
      <div
        v-if="isHomeOpen"
        class="serp-layout-mobile__home"
        data-testid="serp-layout-mobile-home"
      >
        <IndexPage
          mobile
          :product-type="productSearch.type"
          :cities="cities"
          :current-city="currentCity"
          :is-visible-room-button="isVisibleRoomButton"
          @search="onHomeSearch"
          @chat="emit('chat')"
          @change:city="emit('change:city', $event)"
          @track="trackSearchClick($event, 'back')"
        />
      </div>
    </FlipView>

    <div
      v-show="!isSerpHidden"
      class="serp-layout-mobile__serp"
      data-testid="serp-layout-mobile-serp"
    >
      <div
        class="serp-layout-mobile__top"
        data-testid="serp-layout-mobile-top"
      >
        <div
          class="serp-layout-mobile__header"
          data-testid="serp-layout-mobile-header"
        >
          <SerpMobileHeader
            class="serp-layout-mobile__header-content"
            :title="title"
            :subtitle="subtitle"
            @back="onBack"
            @edit="openSearchSheet"
            @chat="emit('chat')"
          />
        </div>

        <div class="serp-layout-mobile__toolbar">
          <div class="serp-layout-mobile__quick-filters">
            <SerpQuickFiltersSkeleton v-if="isLoading" />
            <ClientOnly v-else>
              <SerpQuickFilters
                :sort="sort"
                :filters-count="filtersCount"
                :quick-filters="quickFilters"
                :scroll-left="quickFiltersScrollLeft"
                @filters="openFiltersSheet"
                @sort="openSortSheet"
                @open="openQuickFilterSheet"
                @toggle="toggleQuickFilter"
                @clear="clearQuickFilter"
                @scroll="saveQuickFiltersScroll"
              />
            </ClientOnly>
          </div>
          <SerpSearchProgress
            v-if="isProgressVisible"
            :value="progressValue"
            :stage="progressStage"
          />
        </div>
      </div>

      <HotelsMap
        v-if="isMapOpen"
        class="serp-layout-mobile__map"
        :products="mapProducts"
        :loading="isLoading"
        @list="closeMap"
        @open="openMapHotel"
      />

      <main
        class="serp-layout-mobile__content"
        :class="{ 'serp-layout-mobile__content_hidden': isMapOpen }"
        data-testid="serp-layout-mobile-content"
      >
        <LowPriceCalendar
          :criteria="model"
          :calendar="lowPriceCalendar"
          :loading="isCalendarLoading"
          :products="calendarProducts"
          @select="onCalendarSelect"
        />

        <SerpAlternativeSearchNotice
          v-if="noticeView"
          ref="alternativeSearchNoticeRef"
          :view="noticeView"
          :lead-trigger-reason="noticeLeadTriggerReason"
          @select-city="onSelectDepartureCity"
        />

        <SerpList
          :key="serpListKey"
          class="serp-layout-mobile__list"
          :products="sortedProducts"
          :loading="isLoading"
          :filters-model="filtersModel"
          @open="openListHotel"
          @visible="saveListVisibleCount"
          @view-card="viewCard"
        >
          <template #banner="{ product, cardPosition, totalCards }">
            <slot
              name="banner"
              :product="product"
              :search-criteria="resultsSearchCriteria"
              :card-position="cardPosition"
              :total-cards="totalCards"
              :is-alternative-search="isAlternativeSearchResults"
              :is-short-serp="isShortSerp"
              :search-emirates="searchEmirates"
            />
          </template>
        </SerpList>

        <SerpShortLeadBanner v-if="isShortSerp" />

        <slot />
      </main>

      <FloatingChipButton
        v-if="!isMapOpen && !isLoading && filteredProducts.length"
        :icon="IconName.Map"
        testid="serp-layout-mobile-map-button"
        @click="openMap"
      >
        {{ SERP_MAP_TEXTS.mapButton }}
      </FloatingChipButton>

      <SerpFiltersResetToast
        v-if="filtersResetMessage && products.length && !isMapOpen"
        :title="filtersResetMessage.title"
        :description="filtersResetMessage.description"
        @restore="restoreFilters"
        @close="dismissFiltersReset"
      />

      <SearchFormBottomSheet
        v-if="isSearchSheetOpen"
        v-model="modelDraft"
        :form-url="formUrl"
        :prices="prices"
        :tracking="SEARCH_FORM_TRACKING"
        @search="searchFromForm"
        @close="closeSearchSheet"
        @track="trackSearchClick($event, 'header_edit')"
      />

      <SerpSortSheet
        v-if="isSortSheetOpen"
        v-model="sort"
        @select="serpDataLayer.trackSortSelect"
        @close="closeSortSheet"
      />

      <SerpFiltersMobile
        v-if="isFiltersSheetOpen"
        :filters-groups="filtersGroups"
        :selected-filters="selectedFilters"
        :changed="isFiltersDraftChanged"
        :selected="isFiltersDraftSelected"
        @apply="applyFiltersSheet"
        @close="closeFiltersSheet"
      />

      <SerpQuickFilterSheet
        v-if="quickFilterGroupId"
        :group-id="quickFilterGroupId"
        :group="quickFilterGroup"
        :changed="isQuickFilterDraftChanged"
        @apply="applyFilters"
        @close="closeQuickFilterSheet"
      />

      <SerpSearchExpiredSheet
        v-if="isSearchExpiredSheetVisible"
        @refresh="refreshExpiredSearch"
        @home="openHome"
        @close="dismissSearchExpiration"
      />
    </div>
    <div ref="seoAnchorRef"></div>
    <div
      v-if="pageContent"
      v-show="isSeoVisible && filteredProducts.length >= SERP_SEO_MIN_PRODUCTS"
      data-testid="serp-seo"
    >
      <Typo variant="h-1">{{ pageContent.label }}</Typo>
      <div
        data-testid="serp-seo-content"
        v-html="pageContent.content"
      ></div>
    </div>
  </div>
</template>

<style scoped lang="scss">
  .serp-layout-mobile {
    $top-inset: var(--serp-layout-top-inset, 0px);
    $header-height: 52px;

    background: var(--color-bg-surface);
    display: flex;
    flex-direction: column;
    min-height: calc(100dvh - #{$top-inset});
    overflow-anchor: none;
    padding-bottom: var(--spacing-xxxl);
    position: relative;
    width: 100%;

    &__serp {
      display: contents;
    }

    &__top {
      background: var(--color-bg-primary);
      position: sticky;
      top: $top-inset;
      transition: transform 240ms cubic-bezier(0.25, 0.8, 0.25, 1);
      z-index: 2;
    }

    &_header-collapsed &__top {
      transform: translateY(-$header-height);
    }

    &__header {
      height: $header-height;
    }

    &__header-content {
      transition: opacity 180ms ease;
    }

    &_header-collapsed &__header-content {
      opacity: 0;
    }

    &__quick-filters {
      height: 56px;
    }

    &__toolbar {
      background: var(--color-bg-primary);
      box-shadow: var(--shadow-s);
      clip-path: inset(0 0 -16px);
    }

    &__content {
      flex: 1 1 auto;
    }

    &__content_hidden {
      height: 0;
      left: 0;
      overflow: hidden;
      position: absolute;
      right: 0;
      visibility: hidden;
    }

    &__list {
      padding-bottom: var(--spacing-xs);
      padding-top: var(--spacing-xs);
    }

    &__map {
      flex: 1 1 auto;
      min-height: 0;
    }

    &_map {
      height: calc(100dvh - #{$top-inset});
      overflow: hidden;
    }
  }
</style>
